import { Card, Col, Empty, Row, Select, Spin, Statistic, Table, Tag } from "antd";
import { ColumnsType } from "antd/lib/table";
import locale from "antd/es/date-picker/locale/es_ES";
import { AntdDatePicker, InternalPageLayout } from "components";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import { collection, onSnapshot } from "firebase/firestore";
import { signInAnonymously } from "firebase/auth";
import { auth, db } from "config/firebase";
import { buildStats, formatDuration, LEGACY_QUIZ_ID, normalizeAttempt, QUIZ_COLLECTION, QuizAttempt } from "./quizData";
import { getQuizKey, Quiz, statusLabels, subscribeQuizzes } from "./quizModel";

const { RangePicker } = AntdDatePicker;
const attemptDate = (a: QuizAttempt) => a.startedAt ?? a.createdAt;
import { DurationChart, LetterSpreadChart, QuestionAccuracyChart, TimelineChart } from "./QuizCharts";

const panelStyle = { borderRadius: 10, height: "100%" };
const leadStyle = { color: "#5E5058", fontSize: 13, marginTop: 0 };

const columns: ColumnsType<QuizAttempt> = [
    {
        title: "Participante",
        dataIndex: "name",
        render: (name: string, a) => (
            <div>
                <div style={{ fontWeight: 600 }}>{name}</div>
                <div style={{ fontSize: 12, color: "#8C8C8C" }}>{a.email}</div>
            </div>
        )
    },
    { title: "Origen", dataIndex: "isInternal", render: (internal: boolean) => <Tag color={internal ? "magenta" : "default"}>{internal ? "Good Look" : "Externa"}</Tag> },
    { title: "Aciertos", dataIndex: "correctCount", sorter: (a, b) => a.correctCount - b.correctCount, render: (v: number, a) => `${v} / ${a.totalQuestions}` },
    { title: "Calificación", dataIndex: "grade" },
    { title: "Duración", dataIndex: "durationSeconds", sorter: (a, b) => a.durationSeconds - b.durationSeconds, render: (v: number) => formatDuration(v) },
    { title: "Inicio", dataIndex: "startedAt", render: (d: Date | null) => d ? dayjs(d).format("DD/MM/YYYY HH:mm:ss") : "—" },
    { title: "Fin", dataIndex: "finishedAt", render: (d: Date | null) => d ? dayjs(d).format("DD/MM/YYYY HH:mm:ss") : "—" }
];

const QuizDashboard = () => {
    const [attempts, setAttempts] = useState<QuizAttempt[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
    const [quizzes, setQuizzes] = useState<Quiz[]>([]);
    const [answerKey, setAnswerKey] = useState<Record<string, string> | null>(null);
    const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedQuizId = searchParams.get("quizId");

    useEffect(() => {
        let isMounted = true;
        let unsubscribe: (() => void) | undefined;

        const subscribe = async () => {
            try {
                if (!auth.currentUser) {
                    await signInAnonymously(auth);
                }
                if (!isMounted) return;

                unsubscribe = onSnapshot(
                    collection(db, QUIZ_COLLECTION),
                    (snapshot) => {
                        const data = snapshot.docs
                            .map((document) => normalizeAttempt(document.id, document.data()))
                            .sort((a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0));
                        setAttempts(data);
                        setLastUpdate(new Date());
                        setError(null);
                        setIsLoading(false);
                    },
                    (snapshotError) => {
                        console.error("Quiz snapshot failed", snapshotError);
                        setError("No se pudieron cargar los resultados del quiz.");
                        setIsLoading(false);
                    }
                );
            } catch (authError) {
                console.error("Firebase anonymous auth failed", authError);
                if (isMounted) {
                    setError("No se pudo conectar con Firebase.");
                    setIsLoading(false);
                }
            }
        };

        subscribe();
        return () => {
            isMounted = false;
            if (unsubscribe) unsubscribe();
        };
    }, []);

    useEffect(() => subscribeQuizzes(setQuizzes, (quizError) => console.error("Quizzes snapshot failed", quizError)), []);

    const quizOptions = useMemo(() => {
        const options = quizzes.map((q) => ({
            value: q.id,
            label: `${q.quizDate ? dayjs(q.quizDate).format("DD/MM/YYYY") : "Sin fecha"} · ${q.title}`,
            status: q.status
        }));
        if (attempts.some((a) => a.quizId === LEGACY_QUIZ_ID)) {
            options.push({ value: LEGACY_QUIZ_ID, label: "Quiz inicial (sin cuestionario asignado)", status: "inactive" });
        }
        return options;
    }, [quizzes, attempts]);

    // Sin seleccion explicita: el cuestionario mas reciente que ya tenga intentos.
    const quizId = useMemo(() => {
        if (selectedQuizId && quizOptions.some((o) => o.value === selectedQuizId)) return selectedQuizId;
        const withAttempts = quizOptions.find((o) => attempts.some((a) => a.quizId === o.value));
        return withAttempts?.value ?? quizOptions[0]?.value ?? LEGACY_QUIZ_ID;
    }, [selectedQuizId, quizOptions, attempts]);
    const selectedQuiz = quizzes.find((q) => q.id === quizId);

    useEffect(() => {
        let cancelled = false;
        setAnswerKey(null);
        if (quizId !== LEGACY_QUIZ_ID) {
            getQuizKey(quizId)
                .then((key) => !cancelled && setAnswerKey(key))
                .catch((keyError) => console.error("Quiz key read failed", keyError));
        }
        return () => {
            cancelled = true;
        };
    }, [quizId]);

    const filteredAttempts = useMemo(() => attempts.filter((a) => {
        if (a.quizId !== quizId) return false;
        const date = attemptDate(a);
        if (range?.[0] && (!date || dayjs(date).isBefore(range[0], "day"))) return false;
        if (range?.[1] && (!date || dayjs(date).isAfter(range[1], "day"))) return false;
        return true;
    }), [attempts, quizId, range]);

    const stats = useMemo(() => buildStats(filteredAttempts, answerKey), [filteredAttempts, answerKey]);


    const fastest = stats.attempts.length
        ? stats.attempts.reduce((min, a) => (a.durationSeconds < min.durationSeconds ? a : min))
        : null;
    const topGrade = stats.grades[0];

    return (
        <InternalPageLayout title="Quiz Cultura Good Look">
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 16, fontSize: 12, color: "#8C8C8C" }}>
                <span>
                    <Tag color={error ? "red" : "green"} style={{ marginRight: 8 }}>{error ? "Sin conexión" : "En vivo"}</Tag>
                    {/*Colección {QUIZ_COLLECTION} (Firebase)*/}
                </span>
                {lastUpdate && <span>Actualizado {dayjs(lastUpdate).format("DD/MM/YYYY HH:mm:ss")}</span>}
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 16, alignItems: "center" }}>
                <Select
                    value={quizOptions.length ? quizId : undefined}
                    placeholder="Cuestionario"
                    onChange={(value: string) => setSearchParams({ quizId: value })}
                    style={{ minWidth: 320 }}
                    showSearch
                    optionFilterProp="children"
                >
                    {quizOptions.map((o) => (
                        <Select.Option key={o.value} value={o.value}>
                            {o.label}{o.status === "active" ? " (activo)" : ""}
                        </Select.Option>
                    ))}
                </Select>
                <RangePicker
                    locale={locale}
                    format="DD/MM/YYYY"
                    value={range as any}
                    onChange={(values) => setRange(values as any)}
                    placeholder={["Intentos desde", "Hasta"]}
                />
                {selectedQuiz && <Tag color={statusLabels[selectedQuiz.status].color}>{statusLabels[selectedQuiz.status].label}</Tag>}
            </div>

            <Spin spinning={isLoading} tip="Cargando resultados...">
                {error && <p style={{ color: "#CF1322" }}>{error}</p>}
                {!isLoading && !stats.attempts.length && !error && <Empty description={range ? "No hay intentos en esas fechas" : "Aún no hay intentos para este cuestionario"} />}

                {stats.attempts.length > 0 && (
                    <>
                        <Row gutter={[16, 16]}>
                            <Col xs={12} lg={6}>
                                <Card style={panelStyle}>
                                    <Statistic title="Participantes" value={stats.attempts.length} />
                                    <div style={leadStyle}>{`${stats.externalCount} externas · ${stats.internalCount} de Good Look`}</div>
                                </Card>
                            </Col>
                            <Col xs={12} lg={6}>
                                <Card style={panelStyle}>
                                    <Statistic title="Aciertos promedio" value={Math.round(stats.avgAccuracy * 100)} suffix="%" />
                                    <div style={leadStyle}>{`${stats.questions.length} preguntas`}</div>
                                </Card>
                            </Col>
                            <Col xs={12} lg={6}>
                                <Card style={panelStyle}>
                                    <Statistic title="Tiempo promedio" value={formatDuration(stats.avgDuration)} />
                                    <div style={leadStyle}>
                                        {`min:s · ${stats.questions.length ? Math.round(stats.avgDuration / stats.questions.length) : 0} s por pregunta`}
                                    </div>
                                </Card>
                            </Col>
                            <Col xs={12} lg={6}>
                                <Card style={panelStyle}>
                                    <Statistic title="Calificación" value={`${topGrade?.count ?? 0} / ${stats.attempts.length}`} />
                                    <Tag color="green">{topGrade?.grade}</Tag>
                                </Card>
                            </Col>
                        </Row>

                        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                            <Col xs={24} lg={12}>
                                <Card title="Duración por participante" style={panelStyle}>
                                    {fastest && (
                                        <p style={leadStyle}>
                                            {`${fastest.name} terminó más rápido: ${formatDuration(fastest.durationSeconds)} (${(fastest.durationSeconds / (fastest.totalQuestions || 1)).toFixed(1)} s por pregunta).`}
                                        </p>
                                    )}
                                    <DurationChart attempts={stats.attempts} avg={stats.avgDuration} />
                                </Card>
                            </Col>
                            <Col xs={24} lg={12}>
                                <Card title="Momento en que lo presentaron" style={panelStyle}>
                                    <p style={leadStyle}>Inicio y fin de cada intento, en hora local.</p>
                                    <TimelineChart attempts={stats.attempts} />
                                </Card>
                            </Col>

                            <Col xs={24}>
                                {stats.hasKey ? (
                                    <Card title="Acierto por pregunta" style={panelStyle}>
                                        <p style={leadStyle}>
                                            {`La letra sobre cada barra es la respuesta correcta${answerKey ? "" : ", inferida de los intentos con calificación perfecta"}. Pasa el cursor para ver la pregunta.`}
                                        </p>
                                        <QuestionAccuracyChart questions={stats.questions} />
                                    </Card>
                                ) : (
                                    <Card title="Letras elegidas por pregunta" style={panelStyle}>
                                        <p style={leadStyle}>Sin clave de respuestas disponible: se muestra cómo se repartieron las respuestas.</p>
                                        <LetterSpreadChart questions={stats.questions} />
                                    </Card>
                                )}
                            </Col>
 
                        </Row>

                        <Card title="Intentos" style={{ ...panelStyle, marginTop: 16 }}>
                            <Table
                                rowKey="id"
                                columns={columns}
                                dataSource={stats.attempts}
                                pagination={{ pageSize: 10, hideOnSinglePage: true }}
                                scroll={{ x: 900 }}
                                size="small"
                            />
                        </Card>
                    </>
                )}
            </Spin>
        </InternalPageLayout>
    );
};
export default QuizDashboard;
